import { isMfaChallenge, OutpostClient, type Session, type User } from '@outpost/api-client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { config } from './config';
import { refreshStoredSession, type RefreshTokenStore } from './session';
import { requestPasskey } from './webauthn';

/**
 * Where the refresh token is kept.
 *
 * localStorage is readable by any script on the origin, which is a real
 * trade-off. It is chosen deliberately: the alternative (an HttpOnly cookie)
 * requires the API and dashboard to be same-site, which a self-hosted install
 * cannot assume. The access token is held only in memory to limit exposure, and
 * refresh tokens rotate on every use so a stolen one has a short useful life.
 *
 * Storage is also the only copy. Every tab reads the token from it when needed,
 * because rotation revokes whatever copy a tab might otherwise be holding.
 */
const REFRESH_KEY = 'outpost.refresh_token';

const storedRefreshToken = (): string | null =>
  typeof localStorage !== 'undefined' ? localStorage.getItem(REFRESH_KEY) : null;

const refreshTokenStore: RefreshTokenStore = {
  get: storedRefreshToken,
  remove: () => localStorage.removeItem(REFRESH_KEY),
};

interface AuthContextValue {
  user: User | null;
  client: OutpostClient;
  /** True until the initial session restore has finished. */
  loading: boolean;
  /**
   * Whether this session was established with a second factor or a passkey.
   * Organizations that require MFA accept nothing else, so pages use it to
   * explain rather than to enforce — the server decides every request.
   */
  mfa: boolean;
  /**
   * The identity provider that established this session, or null. Used the same
   * way as {@link mfa}: to explain, never to enforce.
   */
  sso: string | null;
  /**
   * Sign in with a password.
   *
   * Resolves to a challenge when the account has an authenticator, in which
   * case nobody is signed in until {@link completeMfa} succeeds.
   */
  login: (email: string, password: string) => Promise<{ mfaToken: string } | null>;
  /** Finish a sign-in with an authenticator code or a recovery code. */
  completeMfa: (mfaToken: string, code: string) => Promise<void>;
  /** Sign in with a passkey, which never involves a second step. */
  loginWithPasskey: () => Promise<void>;
  /**
   * Send the browser to an identity provider to sign in.
   *
   * Never resolves in the normal case: the page navigates away and comes back
   * at the callback route, which calls {@link completeSso}.
   */
  loginWithSso: (provider: string) => Promise<void>;
  /**
   * Finish a sign-in that came back from an identity provider.
   *
   * Resolves to a challenge when the account has an authenticator, exactly as
   * {@link login} does: the provider stands in for the password, not the code.
   */
  completeSso: (code: string, state: string) => Promise<{ mfaToken: string } | null>;
  /** Send the browser to a provider to connect it to the signed-in account. */
  connectSso: (provider: string) => Promise<void>;
  /** Finish connecting a provider. */
  completeSsoConnect: (code: string, state: string) => Promise<void>;
  /**
   * Adopt a session the server upgraded — after enrolling an authenticator or
   * registering a passkey, both of which prove the factor there and then.
   */
  upgradeSession: (session: Session) => Promise<void>;
  register: (input: {
    email: string;
    name: string;
    password: string;
    organization?: string;
  }) => Promise<void>;
  /** Create an account through an invitation, join its organization, and sign in. */
  registerWithInvite: (input: { token: string; name: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [mfa, setMfa] = useState(false);
  const [sso, setSso] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // A ref rather than state: the client closes over it, and re-creating the
  // client on every token change would cancel in-flight requests.
  const accessToken = useRef<string | null>(null);

  const applySession = useCallback((session: Session) => {
    accessToken.current = session.access_token;
    localStorage.setItem(REFRESH_KEY, session.refresh_token);
    setUser(session.user);
    setMfa(session.mfa);
    setSso(session.sso);
  }, []);

  /** Forget this tab's session without touching the token other tabs share. */
  const forgetSession = useCallback(() => {
    accessToken.current = null;
    setUser(null);
    setMfa(false);
    setSso(null);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(REFRESH_KEY);
    forgetSession();
  }, [forgetSession]);

  // The refresh callback needs the client it is attached to, so the client is
  // held in a ref and read lazily rather than captured during construction.
  const clientRef = useRef<OutpostClient | null>(null);

  // One refresh at a time in this tab. Restoring on load and a 401 can overlap,
  // as can StrictMode's second run of the restore effect, and each would
  // otherwise spend the token the other is about to use.
  const refreshing = useRef<Promise<string | null> | null>(null);

  const refreshSession = useCallback((): Promise<string | null> => {
    refreshing.current ??= refreshStoredSession(refreshTokenStore, (token) =>
      clientRef.current!.refresh(token),
    )
      .then((outcome) => {
        switch (outcome.kind) {
          case 'refreshed':
            applySession(outcome.session);
            return outcome.session.access_token;
          case 'signed-out':
            forgetSession();
            return null;
          case 'unavailable':
            // Stay signed in: the next request tries again.
            return null;
        }
      })
      .finally(() => {
        refreshing.current = null;
      });
    return refreshing.current;
  }, [applySession, forgetSession]);

  const client = useMemo(() => {
    const created = new OutpostClient({
      baseUrl: config.apiUrl,
      getToken: () => accessToken.current,
      onUnauthorized: refreshSession,
    });
    clientRef.current = created;
    return created;
  }, [refreshSession]);

  // Restore a session on first load.
  useEffect(() => {
    let cancelled = false;
    void refreshSession().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshSession]);

  // Signing out in another tab removes the token this one would refresh with, so
  // follow it now rather than at the next request.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if ((event.key === REFRESH_KEY || event.key === null) && storedRefreshToken() === null) {
        forgetSession();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [forgetSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      client,
      loading,
      mfa,
      sso,
      login: async (email, password) => {
        const result = await client.login(email, password);
        if (isMfaChallenge(result)) return { mfaToken: result.mfa_token };
        applySession(result);
        return null;
      },
      completeMfa: async (mfaToken, code) => applySession(await client.completeMfa(mfaToken, code)),
      loginWithPasskey: async () => {
        const options = await client.passkeyLoginOptions();
        applySession(await client.passkeyLogin(await requestPasskey(options)));
      },
      loginWithSso: async (provider) => {
        const { authorization_url, state } = await client.startSso(provider);
        // Remembered so the callback can refuse a state this browser never
        // asked for. Without it, following a link someone else prepared would
        // sign you in as them.
        rememberSsoState(state);
        window.location.assign(authorization_url);
      },
      completeSso: async (code, state) => {
        if (!takeSsoState(state)) {
          throw new Error('That sign-in did not start here. Try signing in again.');
        }
        const result = await client.completeSso(code, state);
        if (isMfaChallenge(result)) return { mfaToken: result.mfa_token };
        applySession(result);
        return null;
      },
      connectSso: async (provider) => {
        const { authorization_url, state } = await client.startSsoConnect(provider);
        rememberSsoState(state);
        window.location.assign(authorization_url);
      },
      completeSsoConnect: async (code, state) => {
        if (!takeSsoState(state)) {
          throw new Error('That request did not start here. Try connecting the account again.');
        }
        await client.completeSsoConnect(code, state);
      },
      upgradeSession: async (session) => {
        // The session being replaced is weaker than the new one and would
        // otherwise sit in the database until it expired.
        const previous = storedRefreshToken();
        if (previous && previous !== session.refresh_token) {
          await client.logout(previous).catch(() => undefined);
        }
        applySession(session);
      },
      register: async (input) => applySession(await client.register(input)),
      registerWithInvite: async (input) => applySession(await client.registerWithInvite(input)),
      logout: async () => {
        const token = storedRefreshToken();
        if (token) {
          // Best effort: the local session is cleared regardless, so a failed
          // call cannot leave the user apparently signed in.
          await client.logout(token).catch(() => undefined);
        }
        clearSession();
      },
    }),
    [user, mfa, sso, client, loading, applySession, clearSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Where a ceremony's state is kept between leaving for the provider and coming
 * back.
 *
 * sessionStorage rather than localStorage: it is scoped to this tab, which is
 * exactly the lifetime of one sign-in, and it does not outlive the browser.
 */
const SSO_STATE_KEY = 'outpost.sso_state';

/** Where to land after the ceremony, since the browser leaves the page. */
const SSO_RETURN_KEY = 'outpost.sso_return_to';

export function rememberSsoState(state: string) {
  sessionStorage.setItem(SSO_STATE_KEY, state);
}

export function rememberSsoReturnTo(path: string) {
  sessionStorage.setItem(SSO_RETURN_KEY, path);
}

/**
 * Where the sign-in was headed before it detoured through the provider.
 *
 * Only ever a path within the dashboard: this comes out of storage rather than
 * off a URL, but treating it as a path regardless costs nothing and means a
 * stray absolute URL can never become an open redirect.
 */
export function takeSsoReturnTo(): string {
  const stored = sessionStorage.getItem(SSO_RETURN_KEY);
  sessionStorage.removeItem(SSO_RETURN_KEY);
  return stored?.startsWith('/') && !stored.startsWith('//') ? stored : '/';
}

/**
 * Spend the stored state, returning whether it was the one presented.
 *
 * Single use either way: a callback that does not match must not be retried
 * against a state that is still sitting there.
 */
export function takeSsoState(state: string): boolean {
  const stored = sessionStorage.getItem(SSO_STATE_KEY);
  sessionStorage.removeItem(SSO_STATE_KEY);
  return stored !== null && stored === state;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
